import { cn } from '@/shadcn-ui/utils';
import { colors } from '@/theme/colors';
import { getDynamicCurrency } from '@/util-conversion/currency';
import { InsurancePlan } from '@/catalog/types';
import {
  RadioGroup,
  RadioGroupItem,
  RadioGroupProps,
  RadioItemLabel,
} from '@/shadcn-ui/radio-group';
import { useMemo, useState } from 'react';
import Check from '@/icons/check';
import Spacer from '@/common/components/spacer';
import PDPPlanHighlightPill from './pdp-plan-highlight-tag';

export interface InsurancePlanRadioGroupProps extends RadioGroupProps {
  variant?: 'summary';
  insurancePlans?: InsurancePlan[];
  selectedInsurancePlanId: string;
  currentInsurancePlanId?: string;
  disabled?: boolean;
  error?: string;
  showRecommended?: boolean;
  disableUpgrade?: boolean;
  disableVariant?: 'dark';
  fullstoryEventDelegate?: () => void;
}

export default function InsurancePlanRadioGroup({
  variant,
  insurancePlans,
  selectedInsurancePlanId,
  currentInsurancePlanId,
  error,
  disabled,
  showRecommended = true,
  disableUpgrade = false,
  disableVariant,
  fullstoryEventDelegate,
  ...props
}: InsurancePlanRadioGroupProps) {
  const currentInsurancePlanPrice = useMemo(() => {
    if (insurancePlans && currentInsurancePlanId) {
      return (
        insurancePlans.find((plan) => String(plan.id) === currentInsurancePlanId)
          ?.price_per_month || null
      );
    }
    return null;
  }, [currentInsurancePlanId, insurancePlans]);

  const [lastPlanName, setLastPlanName] = useState<string | undefined>(undefined);

  const handleInsuranceClick = (_: React.MouseEvent, planName: string) => {
    if (lastPlanName !== planName) {
      setLastPlanName(planName);
      fullstoryEventDelegate?.();
    }
  };

  return (
    <>
      <RadioGroup
        name="insurance_plan_id"
        defaultValue={selectedInsurancePlanId}
        className="gap-[8px]"
        disabled={disabled}
        {...props}
      >
        {insurancePlans
          ? insurancePlans.map((plan) => {
              const isDisabled: boolean =
                disabled ||
                (disableUpgrade &&
                  currentInsurancePlanPrice !== null &&
                  plan.price_per_month > currentInsurancePlanPrice);

              return (
                <RadioItemLabel
                  key={plan.name}
                  htmlFor={plan.name}
                  selected={selectedInsurancePlanId === String(plan.id)}
                  className={cn('p-0 transition-all', {
                    'bg-flexcar-neutral90 ring-transparent':
                      isDisabled && disableVariant === 'dark',
                    'shadow-shadow30 ring-flexcar-blue40':
                      selectedInsurancePlanId === String(plan.id),
                    'shadow-shadow2': selectedInsurancePlanId !== String(plan.id),
                  })}
                  disabled={isDisabled}
                  onClick={(e) => handleInsuranceClick(e, plan.name)}
                >
                  <div
                    className={cn(
                      'flex flex-col items-end rounded-2xl bg-[linear-gradient(180deg,_#FFFFFF,_#FBFBFB)] pt-[16px]',
                      {
                        'pb-[16px]': !plan.is_recommended || !showRecommended,
                        'pb-0': plan.is_recommended && showRecommended,
                      }
                    )}
                  >
                    <div className="w-full px-[16px]">
                      {currentInsurancePlanId === String(plan.id) ? (
                        <>
                          <div
                            className={cn(
                              'body3-strong inline rounded-full bg-flexcar-neutral90 px-2 py-1 text-flexcar-neutral50',
                              {
                                'bg-flexcar-neutral100':
                                  isDisabled && disableVariant === 'dark',
                              }
                            )}
                          >
                            Current
                          </div>
                          <Spacer variant="height8" />
                        </>
                      ) : null}
                      <div className="flex flex-row justify-between">
                        <RadioGroupItem
                          value={String(plan.id)}
                          id={plan.name}
                          className="hidden"
                          selected={selectedInsurancePlanId === String(plan.id)}
                          disabled={isDisabled}
                        />
                        <p
                          className={cn('body-strong', {
                            'text-flexcar-neutral70':
                              isDisabled && disableVariant !== 'dark',
                            'text-flexcar-neutral50':
                              isDisabled && disableVariant === 'dark',
                          })}
                        >
                          {plan.name}
                        </p>
                        <p
                          className={cn('body', {
                            'text-flexcar-neutral70':
                              isDisabled && disableVariant !== 'dark',
                            'text-flexcar-neutral50':
                              isDisabled && disableVariant === 'dark',
                          })}
                        >
                          {getDynamicCurrency(plan.price_per_month)}/mo
                        </p>
                      </div>
                      <Spacer variant="height12" />
                      {variant === 'summary' ? (
                        <div
                          className={cn('body2', {
                            'text-flexcar-neutral70':
                              isDisabled && disableVariant !== 'dark',
                            'text-flexcar-neutral50':
                              isDisabled && disableVariant === 'dark',
                          })}
                        >
                          {plan.summary}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-[8px]">
                          <div className="flex flex-col gap-[4px]">
                            {plan.details.map((detail) => (
                              <div className="flex items-center gap-[4px]" key={detail}>
                                <Check
                                  fillPrimary={isDisabled ? colors.neutral70 : undefined}
                                />
                                <p
                                  className={cn('body2', {
                                    'text-flexcar-neutral70':
                                      isDisabled && disableVariant !== 'dark',
                                    'text-flexcar-neutral50':
                                      isDisabled && disableVariant === 'dark',
                                  })}
                                >
                                  {detail}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {plan.is_recommended && showRecommended ? (
                      <PDPPlanHighlightPill text="Recommended" disabled={isDisabled} />
                    ) : null}
                  </div>
                </RadioItemLabel>
              );
            })
          : null}
      </RadioGroup>
      {error ? (
        <p id="plan-error-text" className="body3 text-flexcar-red40">
          {error}
        </p>
      ) : null}
    </>
  );
}
